import { FunctionComponent } from "react";

import KeyValue from "components/KeyValue";
import { Spinner } from "components/Spinner";
import { Magnet, Trash } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import { CLOUD_INTEGRATIONS } from "shared/CloudIntegration/types";
import {
  AwsIntegrationIntentProjectAttachment,
  StackAwsIntegrationAttachment,
  StackAzureIntegrationAttachment,
  StackIntegrationGcp,
} from "types/generated";
import { getAttachedLabel } from "utils/labels";

import AWSFields from "./AWSFields";
import AzureFields from "./AzureFields";
import GCPFields from "./GCPFields";
import SwitchIcon from "./SwitchIcon";
import {
  AUTO_ATTACHMENT_KEY,
  AUTO_ATTACHMENT_READ_KEY,
  AUTO_ATTACHMENT_WRITE_KEY,
} from "../../../constants/labels";

type AttachCloudDetailsCardProps = {
  attachedIntegration:
    | StackAwsIntegrationAttachment
    | StackAzureIntegrationAttachment
    | StackIntegrationGcp
    | AwsIntegrationIntentProjectAttachment;
  attachedIntegrationType: CLOUD_INTEGRATIONS;
  cloudConfig: Array<{
    name: string;
    logo: FunctionComponent;
    type: CLOUD_INTEGRATIONS;
  }>;
  className?: string;
  readOnly?: boolean;
  onDetach?: () => void;
  detachLoading?: boolean;
  stackLabels?: string[];
};

const AttachCloudDetailsCard = ({
  attachedIntegration,
  attachedIntegrationType,
  cloudConfig,
  className,
  readOnly,
  onDetach,
  detachLoading,
  stackLabels,
}: AttachCloudDetailsCardProps) => {
  const config = cloudConfig.find(({ type }) => type === attachedIntegrationType);
  const [isCollapsed, toggle] = useToggle(true);

  if (!config) {
    return null;
  }

  return (
    <CardCollapsible
      fullWidth
      title={`${config.name} integration`}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      ariaLevel={3}
      ariaLabel={`${config.name} integration`}
      className={className}
      headerSuffix={
        "isAutoattached" in attachedIntegration && attachedIntegration.isAutoattached ? (
          <Tag
            tag={getAttachedLabel(stackLabels ?? [], attachedIntegration.labels ?? [], [
              AUTO_ATTACHMENT_KEY,
              AUTO_ATTACHMENT_READ_KEY,
              AUTO_ATTACHMENT_WRITE_KEY,
            ])}
            icon={Magnet}
          />
        ) : (
          <ButtonIcon
            disabled={detachLoading || readOnly}
            forceTooltip={readOnly}
            icon={detachLoading ? Spinner : Trash}
            onPress={onDetach}
            variant={detachLoading ? "ghost" : "ghostDanger"}
          >
            {readOnly ? "You don't have permission to detach this integration" : "Detach"}
          </ButtonIcon>
        )
      }
    >
      <>
        {attachedIntegrationType === CLOUD_INTEGRATIONS.AWS && (
          <AWSFields integration={attachedIntegration as StackAwsIntegrationAttachment} />
        )}

        {attachedIntegrationType === CLOUD_INTEGRATIONS.Azure && (
          <AzureFields integration={attachedIntegration as StackAzureIntegrationAttachment} />
        )}

        {attachedIntegrationType === CLOUD_INTEGRATIONS.GCP && (
          <GCPFields integration={attachedIntegration as StackIntegrationGcp} />
        )}

        {"read" in attachedIntegration && "write" in attachedIntegration && (
          <>
            <KeyValue align="center" name="Read">
              <SwitchIcon checked={attachedIntegration.read} />
            </KeyValue>
            <KeyValue align="center" name="Write">
              <SwitchIcon checked={attachedIntegration.write} />
            </KeyValue>
          </>
        )}
      </>
    </CardCollapsible>
  );
};

export default AttachCloudDetailsCard;
